
import React from "react";
import { X, CheckCircle, XCircle, Clock } from "lucide-react";

const EmailDetailModal = ({ email, onClose }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      sent: { icon: CheckCircle, color: "bg-green-100 text-green-800", label: "Sent" },
      failed: { icon: XCircle, color: "bg-red-100 text-red-800", label: "Failed" },
      pending: { icon: Clock, color: "bg-yellow-100 text-yellow-800", label: "Pending" }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Email Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status Badge */}
          <div>
            <label className="text-sm font-medium text-gray-500">Status</label>
            <div className="mt-2">
              {getStatusBadge(email.status)}
            </div>
          </div>

          {/* Recipients */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">To</label>
              <p className="mt-1 text-sm text-gray-900 break-words">
                {Array.isArray(email.to)
                  ? email.to.join(", ")
                  : email.to || "-"}
              </p>
            </div>
            {email.cc && (
              <div>
                <label className="text-sm font-medium text-gray-500">CC</label>
                <p className="mt-1 text-sm text-gray-900 break-words">
                  {Array.isArray(email.cc)
                    ? email.cc.join(", ")
                    : email.cc}
                </p>
              </div>
            )}
            {email.bcc && (
              <div>
                <label className="text-sm font-medium text-gray-500">BCC</label>
                <p className="mt-1 text-sm text-gray-900 break-words">
                  {Array.isArray(email.bcc)
                    ? email.bcc.join(", ")
                    : email.bcc}
                </p>
              </div>
            )}
          </div>

          {/* Subject */}
          {email.subject && (
            <div>
              <label className="text-sm font-medium text-gray-500">Subject</label>
              <p className="mt-1 text-base font-medium text-gray-900">{email.subject}</p>
            </div>
          )}

          {/* Send Time */}
          <div>
            <label className="text-sm font-medium text-gray-500">Sent At</label>
            <p className="mt-1 text-sm text-gray-900">
              {email.sentAt
                ? new Date(email.sentAt).toLocaleString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })
                : new Date(email.createdAt || Date.now()).toLocaleString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              }
            </p>
          </div>

          {/* Description/Content */}
          <div>
            <label className="text-sm font-medium text-gray-500">Email Content</label>
            <div
              className="mt-2 p-4 border border-gray-200 rounded-lg bg-gray-50 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: email.description || email.html || "<p>No content</p>" }}
            />
          </div>

          {/* Delivery Info */}
          {email.deliveryInfo && Object.keys(email.deliveryInfo).length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-500">Delivery Information</label>
              <div className="mt-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                {email.deliveryInfo.opened !== undefined && (
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Opened:</span> {email.deliveryInfo.opened ? 'Yes' : 'No'}
                  </p>
                )}
                {email.deliveryInfo.clicked !== undefined && (
                  <p className="text-sm text-gray-700 mt-1">
                    <span className="font-medium">Clicked:</span> {email.deliveryInfo.clicked ? 'Yes' : 'No'}
                  </p>
                )}
                {email.deliveryInfo.error && (
                  <p className="text-sm text-red-700 mt-1">
                    <span className="font-medium">Error:</span> {email.deliveryInfo.error}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailDetailModal;